import { getLogoAssetUrl } from "../Preview/Block/BlockType/BlockParts/logoAssetUrl";
import { getLogoRows, logoHasAsset } from "./layout/blocks/logo";
import { BULLET_FONT_FAMILY, resolveFont, toCanvasFont } from "./layout/text";

const registry = new Map();

/**
 * Loaded-image lookup handed to layout and paint. Only fully decoded,
 * CORS-clean images are returned so canvas exports never get tainted.
 */
export const imageStore = {
  get(src) {
    const entry = registry.get(src);
    return entry?.status === "loaded" ? entry : undefined;
  },
};

export function loadImage(src, { crossOrigin = "anonymous" } = {}) {
  if (!src) return Promise.resolve(null);
  const existing = registry.get(src);
  if (existing) return existing.promise;

  const entry = { status: "loading", image: null, width: 0, height: 0 };
  entry.promise = new Promise((resolve) => {
    if (typeof Image === "undefined") {
      entry.status = "error";
      resolve(null);
      return;
    }
    const image = new Image();
    if (crossOrigin) image.crossOrigin = crossOrigin;
    image.decoding = "async";
    image.onload = () => {
      entry.status = "loaded";
      entry.image = image;
      entry.width = image.naturalWidth;
      entry.height = image.naturalHeight;
      resolve(entry);
    };
    image.onerror = () => {
      entry.status = "error";
      resolve(null);
    };
    image.src = src;
  });
  registry.set(src, entry);
  return entry.promise;
}

export const collectLogoSources = (credits = []) => {
  const sources = new Set();
  (credits || []).forEach((credit) => {
    if (credit?.options?.block_type !== "logo") return;
    getLogoRows(credit.items).forEach((row) => {
      (row || []).forEach((logo) => {
        if (logoHasAsset(logo)) sources.add(getLogoAssetUrl(logo));
      });
    });
  });
  return [...sources];
};

export const preloadCreditImages = (credits) =>
  Promise.all(collectLogoSources(credits).map((src) => loadImage(src)));

export const getPendingImageCount = (credits) =>
  collectLogoSources(credits).filter((src) => {
    const entry = registry.get(src);
    return !entry || entry.status === "loading";
  }).length;

/** Request every face the scene uses so measurement sees real fonts. */
export async function loadSceneFonts(fontStyle = {}) {
  if (typeof document === "undefined" || !document.fonts?.load) return;
  const specs = new Set();
  Object.values(fontStyle || {}).forEach((style) => {
    if (style && typeof style === "object") {
      specs.add(toCanvasFont(resolveFont(style)));
    }
  });
  specs.add(`400 18px ${BULLET_FONT_FAMILY}`);
  specs.add(`700 18px ${BULLET_FONT_FAMILY}`);
  await Promise.all(
    [...specs].map((spec) => document.fonts.load(spec).catch(() => null)),
  );
  await document.fonts.ready;
}
